import { LegacyCard, ChoiceList, FormLayout, Layout, Page, LegacyStack, TextField, Select } from "@shopify/polaris";
import { useState, useCallback, useEffect } from "react";
import { SkeletonPageCustom } from "./SkeletonPageCustom";
import { FetchCustom } from './FetchCustom';
import { useHostNavigate } from "../hooks";

export function Advanced(props) {
    const navigate = useHostNavigate();

    const [loading, setLoading] = useState(true);
    const [initState, setInitState] = useState({});
    const [model, setModel] = useState({});
    const [cacheEntities, setCacheEntities] = useState([]);
    const [flushing, setFlushing] = useState(false);
    const [deletingShop, setDeletingShop] = useState(false);
    const [shopToDelete, setShopToDelete] = useState('');
    const [redirectUrl, setRedirectUrl] = useState('');

    const fetch = FetchCustom();

    const populateData = async () => {
        props.setIsLoading(true);
        const data = await fetch('api/home/advanced');

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            var dataJsonString = JSON.stringify(data);

            setInitState(JSON.parse(dataJsonString));
            setModel(JSON.parse(dataJsonString));
            setLoading(false);
        }
    };

    const saveChanges = async () => {
        props.setIsLoading(true);
        const data = await fetch('api/home/saveAdvanced', model);
        props.setIsLoading(false);

        props.setIsDirty(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setInitState(JSON.parse(JSON.stringify(model)));
            props.changeToastActive(true);
        }
    };

    const discardChanges = async () => {
        setModel(Object.assign({}, initState));
        props.setIsDirty(false);
    };

    const flushCache = async () => {
        setFlushing(true);

        const data = await fetch('api/home/flushCache', cacheEntities);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setFlushing(false);
            setCacheEntities([]);

            var message = "Successfully flushed";
            props.changeToastActive(true, message);
        }
    };

    const deleteShop = async () => {
        setDeletingShop(true);

        const data = await fetch('api/home/deleteShop', { myShopifyDomain: shopToDelete });

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setDeletingShop(false);
            setShopToDelete('');

            var message = data ? "Successfully deleted" : "Error deleting";
            props.changeToastActive(true, message);
        }
    };

    const handleChange = useCallback(
        (fieldName, newValue) => {
            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model[fieldName] = newValue;
                return model;
            });
        },
        [],
    );

    const handleCacheEntitiesChange = useCallback(
        (newValue) => setCacheEntities(newValue),
        [],
    );

    const handleShopToDeleteChange = useCallback(
        (newValue) => setShopToDelete(newValue),
        [],
    );

    useEffect(() => {
        document.title = "Advanced | Stack Discounts";
        populateData();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (JSON.stringify(model) !== '{}') {
            props.setIsDirty(JSON.stringify(initState) !== JSON.stringify(model));
        }
        // eslint-disable-next-line
    }, [model, initState]);

    useEffect(() => {
        if (props.triggerSave) {
            props.setTriggerSave(false);
            saveChanges();
        }
        // eslint-disable-next-line
    }, [props.triggerSave]);

    useEffect(() => {
        if (props.triggerDiscard) {
            props.setTriggerDiscard(false);
            discardChanges();
        }
        // eslint-disable-next-line
    }, [props.triggerDiscard]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    return (
        loading ?
            <SkeletonPageCustom />
            :
            <Page title={"Advanced settings"}>
                <Layout>
                    <Layout.Section>
                        <LegacyCard sectioned title="General settings"
                            primaryFooterAction={{ content: 'Save', loading: props.isLoading, onAction: () => { saveChanges() } }}>
                            <FormLayout>
                                <TextField
                                    value={model.customCSS != null ? model.customCSS : ""}
                                    onChange={(newValue) => handleChange('customCSS', newValue)}
                                    label="Custom css"
                                    multiline={4}
                                />
                                <TextField
                                    value={model.customJs != null ? model.customJs : ""}
                                    onChange={(newValue) => handleChange('customJs', newValue)}
                                    label="Custom js"
                                    multiline={4}
                                />
                            </FormLayout>
                        </LegacyCard>
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard sectioned title="Redis cache flush"
                            primaryFooterAction={{ content: 'Flush selected', loading: flushing, disabled: cacheEntities.length === 0, onAction: () => { flushCache() } }}>
                            <LegacyStack>
                                <ChoiceList
                                    allowMultiple
                                    choices={[
                                        {
                                            label: 'App and plans',
                                            value: 1,
                                        },
                                        {
                                            label: 'All subscriptions',
                                            value: 3,
                                        },
                                        {
                                            label: 'Rating',
                                            value: 4,
                                        },
                                    ]}
                                    selected={cacheEntities}
                                    onChange={(newValue) => handleCacheEntitiesChange(newValue)}
                                />
                            </LegacyStack>
                        </LegacyCard>
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard sectioned title="Delete shop"
                            primaryFooterAction={{ content: 'Delete', loading: deletingShop, disabled: shopToDelete === '', onAction: () => { deleteShop() } }}>
                            <FormLayout>
                                <Select
                                    label="My shopify domain"
                                    placeholder="Select"
                                    options={model.testShops}
                                    value={shopToDelete}
                                    onChange={(newValue) => handleShopToDeleteChange(newValue)}
                                />
                            </FormLayout>
                        </LegacyCard>
                    </Layout.Section>
                </Layout>
            </Page >
    );
}